import type { CSSProperties } from 'react';
import type { AssessmentMode, EffortSplit, PrimaryEffort, SubmitResponse, Verdict } from '../types.js';

interface ResultsProps {
  result: SubmitResponse;
  mode?: AssessmentMode;
}

const VERDICT_KICKER: Record<Verdict, string> = {
  enterprise_ai_adoption: 'Enterprise AI Adoption',
  ai_driven_solution: 'AI Driven Solution',
  data_foundation: 'Data Foundation',
  no_fit: 'Not a Fit',
};

const EFFORT_LABEL: Record<PrimaryEffort, string> = {
  ai: 'AI-led',
  data: 'Data-led',
  balanced: 'Balanced',
};

export function Results({ result, mode = 'evaluate' }: ResultsProps): React.JSX.Element {
  const { scores, recommendation, verdict } = result;
  const hasBenefits = (recommendation.benefits?.length ?? 0) > 0;
  const hasReasons = (recommendation.no_fit_reasons?.length ?? 0) > 0;

  return (
    <div className="tbo-results">
      {mode === 'discover' && verdict !== 'no_fit' && (
        <p className="tbo-result-intro">Your highest-value AI opportunity</p>
      )}
      <div className={`tbo-banner v-${verdict}`}>
        <span className="tbo-kicker">{VERDICT_KICKER[verdict]}</span>
        <h2 className="tbo-headline">{result.verdict_headline}</h2>
      </div>

      <div className="tbo-card tbo-scoregrid">
        <div className="tbo-fit">
          <div
            className="tbo-gauge"
            style={
              {
                ['--pct']: scores.ai_fit_overall,
                ['--fit-color']: scores.ai_fit_overall > 70 ? 'var(--green)' : 'var(--primary)',
              } as CSSProperties
            }
            role="img"
            aria-label={`AI Fit ${scores.ai_fit_overall} percent`}
          >
            <div className="tbo-gauge-hole">
              <span className="tbo-fit-num">{scores.ai_fit_overall}%</span>
              <span className="tbo-fit-lbl">AI Fit</span>
            </div>
          </div>
          <div className="tbo-pattern">{result.pattern.label}</div>
        </div>
        <div className="tbo-bars">
          <Bar label="Feasibility" pct={scores.feasibility} />
          <Bar label="ROI" pct={scores.roi} />
          <Bar label="Adoption ease" pct={scores.adoption_ease} />
        </div>
      </div>

      <section className="tbo-card">
        <h3 className="tbo-h3">What we found</h3>
        <ul className="tbo-found">
          {result.teaser_insights.map((insight, i) => (
            <li key={i}>{insight}</li>
          ))}
        </ul>
      </section>

      <section className="tbo-card">
        <h3 className="tbo-h3">Our recommendation</h3>
        <div className="tbo-rec-label">{recommendation.label}</div>
        <p className="tbo-rec-desc">{recommendation.description}</p>
        {hasBenefits && (
          <ul className="tbo-benefits">
            {recommendation.benefits!.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        )}
        {hasReasons && (
          <ul className="tbo-reasons">
            {recommendation.no_fit_reasons!.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        )}
        <SolutionFocus primary={result.primary_effort} split={result.effort_split} />
      </section>

      <div className="tbo-onepager">
        <a
          className="tbo-btn tbo-btn-outline"
          href={result.onepager_url}
          target="_blank"
          rel="noreferrer"
        >
          ⬇ Download your one-pager (PDF)
        </a>
        <span className="tbo-emailed">
          We've also emailed a copy to you.{' '}
          <strong>If it hasn't arrived in a few minutes, check your spam or junk folder</strong> —
          automated mail sometimes lands there.
        </span>
      </div>

      <section className="tbo-card tbo-next">
        <h3 className="tbo-h3">Your next step</h3>
        <p>{result.next_step}</p>
      </section>

      {result.meeting_url && (
        <section className="tbo-card tbo-booking">
          <h3 className="tbo-h3">Book your discovery call</h3>
          <iframe
            className="tbo-meetings"
            src={result.meeting_url}
            title="Book a discovery call"
            loading="lazy"
          />
        </section>
      )}
    </div>
  );
}

/** Where the delivery work sits: a single stacked bar split into Data vs AI effort.
 *  Labels carry identity too (never colour alone). Falls back to a text line if the
 *  agent didn't supply a numeric split. */
function SolutionFocus({
  primary,
  split,
}: {
  primary: PrimaryEffort;
  split?: EffortSplit;
}): React.JSX.Element {
  const label = EFFORT_LABEL[primary];
  const total = split ? split.ai + split.data : 0;
  if (!split || !Number.isFinite(total) || total <= 0) {
    return (
      <p className="tbo-sf-plain">
        Solution focus: <strong>{label}</strong>
      </p>
    );
  }
  const ai = Math.round((split.ai / total) * 100);
  const data = 100 - ai;
  return (
    <div className="tbo-solution-focus">
      <div className="tbo-sf-top">
        <span className="tbo-sf-title">Solution focus</span>
        <span className="tbo-sf-tag">{label}</span>
      </div>
      <div
        className="tbo-split"
        role="img"
        aria-label={`Solution effort: ${data}% data foundation, ${ai}% AI build`}
      >
        {data > 0 && <div className="tbo-split-seg tbo-seg-data" style={{ width: `${data}%` }} />}
        {ai > 0 && (
          <div
            className={`tbo-split-seg tbo-seg-ai${data > 0 ? ' tbo-seg-gap' : ''}`}
            style={{ width: `${ai}%` }}
          />
        )}
      </div>
      <div className="tbo-split-legend">
        <span className="tbo-legend-item">
          <i className="tbo-dot tbo-dot-data" />
          Data foundation <b>{data}%</b>
        </span>
        <span className="tbo-legend-item">
          <i className="tbo-dot tbo-dot-ai" />
          AI build <b>{ai}%</b>
        </span>
      </div>
    </div>
  );
}

function Bar({ label, pct }: { label: string; pct: number }): React.JSX.Element {
  const width = Math.max(0, Math.min(100, pct));
  const color = pct > 60 ? 'var(--green)' : 'var(--primary)';
  return (
    <div className="tbo-bar">
      <div className="tbo-bar-top">
        <span className="tbo-bar-label">{label}</span>
        <span className="tbo-bar-val">{pct}%</span>
      </div>
      <div className="tbo-track">
        <div className="tbo-fill" style={{ width: `${width}%`, background: color }} />
      </div>
    </div>
  );
}
